/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get, ember/no-observers, warp-drive/no-legacy-request-patterns */
import { observes } from '@ember-decorators/object';
import { tracked } from '@glimmer/tracking';
// Extends from
import Service, { service } from '@ember/service';
// External Imports
import config from 'linced-app/config/environment';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import PersonType from '@linc-technologies/linced-common/enums/person-type';

export default class SchoolSchemaService extends Service {
  // Services
  @service
  fetch;

  @service
  session;

  @service
  store;

  // State
  @tracked
  schemaFetched = false;

  @tracked
  jurisdictionalIdFetched = false;
  @tracked
  jurisdictionalIdField = null;

  // _schemaLoad is the schema load that loadSchema started.
  _schemaLoad = null;

  @observes('session.activeSchool')
  _schoolWatcher() {
    this.schemaFetched = false;
    this._schemaLoad = null;
    this.jurisdictionalIdFetched = false;
    this.jurisdictionalIdField = null;
  }

  /**
   * _getSchema pulls down the school's people schema and fields.
   *
   * @return {Promise<void>}
   * @private
   */
  _getSchema() {
    return this.store
      .query('people-schema/group', { include: 'fields' })
      .then(() => {
        this.schemaFetched = true;
      });
  }

  /**
   * _getJurisdictionalIdField pulls down the school's jurisdictional ID field.
   *
   * @return {Promise<void>}
   * @private
   */
  _getJurisdictionalIdField() {
    return this.fetch
      .request(
        `${config.APP.apiHost}/people-schema/${config.APP.apiPath}/fields/jurisdictionalId`,
      )
      .then((resp) => {
        this.store.pushPayload('people-schema/field', resp);

        this.jurisdictionalIdField = this.store.peekRecord(
          'people-schema/field',
          resp.field.id,
        );

        this.jurisdictionalIdFetched = true;
      });
  }

  /**
   * getField returns the first field, or undefined if not found, that exists
   * in the people schema based on the provided person type and field key.
   * It loads the people schema first if necessary.
   *
   * @param personType
   * @param fieldKey
   * @return {Promise<object|undefined>}
   */
  async getField(personType, fieldKey) {
    if (!this.schemaFetched) {
      await this.loadSchema();
    }
    return this.peekField(personType, fieldKey);
  }

  /**
   * peekField is getField for a getter or helper. It gives undefined until the
   * people schema is loaded, and starts the load. `schemaFetched` is tracked,
   * thus the getter runs again after the load.
   *
   * @param personType
   * @param fieldKey
   * @return {object|undefined}
   */
  peekField(personType, fieldKey) {
    if (!this.schemaFetched) {
      void this.loadSchema();
      return;
    }
    let schoolId = this.get('session.activeSchool');
    return this.store
      .peekAll('people-schema/field')
      .find(
        (field) =>
          field.key === fieldKey &&
          field.personType === personType &&
          field.get('school.id') === schoolId,
      );
  }

  /**
   * loadSchema loads the people schema one time for the active school. The
   * load starts after the current render, thus a getter can call it. When the
   * load fails, the next call tries again.
   *
   * @return {Promise<void>}
   */
  loadSchema() {
    this._schemaLoad ??= Promise.resolve()
      .then(() => this._getSchema())
      .catch((error) => {
        this._schemaLoad = null;
        throw error;
      });
    return this._schemaLoad;
  }

  getLearnerGenderField() {
    let key = 'core:genderLegal';
    if (this.get('session.school.jurisdiction') === Jurisdiction.NZ_MOE) {
      key = 'nzMOE:gender';
    }
    return this.getField(PersonType.LEARNER, key);
  }

  /**
   * getJurisdictionalIdField returns the school's Jurisdictional ID field.
   *
   * @return {Promise<object|undefined>}
   */
  async getJurisdictionalIdField() {
    if (!this.jurisdictionalIdFetched) {
      await this._getJurisdictionalIdField();
    }
    return this.jurisdictionalIdField;
  }

  getPeopleSchema() {
    let store = this.store;
    let promise = Promise.resolve();
    let schoolId = this.get('session.activeSchool');
    if (!this.schemaFetched) {
      promise = promise.then(() => this._getSchema());
    }
    return promise.then(() => {
      return store
        .peekAll('people-schema/group')
        .filter((group) => group.get('school.id') === schoolId);
    });
  }

  newPerson(type) {
    let store = this.store;
    let schoolId = this.get('session.activeSchool');
    let person = store.createRecord('person', {
      jurisdiction: this.get('session.school.jurisdiction'),
      type: type || PersonType.LEARNER,
      fieldsArray: [],
    });
    switch (type) {
      case PersonType.STAFF:
        person.set('staff', store.createFragment('people/staff', { schoolId }));
        break;
      case PersonType.CAREGIVER:
        person.set(
          'caregiver',
          store.createFragment('people/caregiver', { schools: [schoolId] }),
        );
        break;
      case PersonType.DOCTORS_AND_SPECIALISTS:
        person.set(
          'medical',
          store.createFragment('people/medical', { schools: [schoolId] }),
        );
        break;
      default:
        person.set(
          'learner',
          store.createFragment('people/learner', { schoolId }),
        );
    }

    return person;
  }
}
